// =============================================================================
// Aggregation Results Viewer Component
// =============================================================================

import * as React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  Table2,
  LineChart as LineChartIcon,
  BarChart3,
  X,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
  Filter,
  Plus,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { AggregationConfig, AggregationResult, AggregationFilters } from '../lib/api.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Select } from './ui/select.js';

// =============================================================================
// Types
// =============================================================================

export type ViewMode = 'table' | 'timeline' | 'bar';

export interface AggregationResultsViewerProps {
  /** Aggregation results */
  results: AggregationResult[];
  /** The aggregation config */
  config: AggregationConfig;
  /** Time range info */
  timeRange?: { from: string; to: string };
  /** Currently applied filters */
  appliedFilters?: AggregationFilters | null;
  /** Callback to close the viewer */
  onClose: () => void;
  /** Callback to re-compute with filters */
  onApplyFilters?: (filters: AggregationFilters | null) => void;
  /** Whether currently computing/loading */
  isComputing?: boolean;
  /** Custom class name */
  className?: string;
}

// =============================================================================
// Color Palette for Charts
// =============================================================================

const CHART_COLORS = [
  '#8b5cf6', // purple
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#3b82f6', // blue
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

// =============================================================================
// Utility Functions
// =============================================================================

function formatMetricValue(value: number | undefined, alias: string): string {
  if (value === undefined || value === null) return '-';

  // Check if it's a rate/percentage
  if (alias.toLowerCase().includes('rate') || alias.toLowerCase().includes('pct') || alias.toLowerCase().includes('percent')) {
    return `${value.toFixed(2)}%`;
  }

  // Check if it's a duration/latency in ms
  if (alias.toLowerCase().includes('latency') || alias.toLowerCase().includes('duration') || alias.toLowerCase().includes('time')) {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}s`;
    }
    return `${value.toFixed(0)}ms`;
  }

  // Format large numbers with separators
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  return value.toFixed(2);
}

function formatTimestamp(timestamp: string, bucketSize: string): string {
  const date = new Date(timestamp);

  // Format based on bucket size
  if (bucketSize === '1d') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (bucketSize === '6h' || bucketSize === '1h') {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  // For smaller buckets
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function calculateTrend(values: number[]): 'up' | 'down' | 'flat' {
  if (values.length < 2) return 'flat';
  const firstHalf = values.slice(0, Math.floor(values.length / 2));
  const secondHalf = values.slice(Math.floor(values.length / 2));
  const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
  const change = ((secondAvg - firstAvg) / (firstAvg || 1)) * 100;
  if (change > 5) return 'up';
  if (change < -5) return 'down';
  return 'flat';
}

// =============================================================================
// View Mode Toggle Component
// =============================================================================

interface ViewModeToggleProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}

function ViewModeToggle({ mode, onChange }: ViewModeToggleProps) {
  const modes: Array<{ value: ViewMode; icon: React.ReactNode; label: string }> = [
    { value: 'table', icon: <Table2 className="h-4 w-4" />, label: 'Table' },
    { value: 'timeline', icon: <LineChartIcon className="h-4 w-4" />, label: 'Timeline' },
    { value: 'bar', icon: <BarChart3 className="h-4 w-4" />, label: 'Bar Chart' },
  ];

  return (
    <div className="flex rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary)] p-0.5">
      {modes.map(({ value, icon, label }) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all',
            mode === value
              ? 'bg-[var(--color-background)] text-[var(--color-foreground)] shadow-sm'
              : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
          )}
          title={label}
        >
          {icon}
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

// =============================================================================
// Metric Summary Cards
// =============================================================================

interface MetricSummaryCardsProps {
  results: AggregationResult[];
  metricKeys: string[];
}

function MetricSummaryCards({ results, metricKeys }: MetricSummaryCardsProps) {
  if (metricKeys.length === 0) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {metricKeys.slice(0, 4).map((key, index) => {
        const values = results.map((r) => r.metrics[key]).filter((v) => v !== undefined) as number[];
        const total = values.reduce((a, b) => a + b, 0);
        const avg = values.length > 0 ? total / values.length : 0;
        const max = Math.max(...values, 0);
        const min = Math.min(...values, Infinity);
        const trend = calculateTrend(values);

        return (
          <div
            key={key}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--color-muted-foreground)]">{key}</span>
              {trend === 'up' && (
                <div className="flex items-center gap-1 text-emerald-400">
                  <TrendingUp className="h-4 w-4" />
                </div>
              )}
              {trend === 'down' && (
                <div className="flex items-center gap-1 text-red-400">
                  <TrendingDown className="h-4 w-4" />
                </div>
              )}
              {trend === 'flat' && (
                <div className="flex items-center gap-1 text-[var(--color-muted-foreground)]">
                  <Minus className="h-4 w-4" />
                </div>
              )}
            </div>
            <div className="mt-2">
              <span
                className="text-2xl font-bold"
                style={{ color: CHART_COLORS[index % CHART_COLORS.length] }}
              >
                {formatMetricValue(key.includes('rate') || key.includes('avg') ? avg : total, key)}
              </span>
            </div>
            <div className="mt-2 flex gap-4 text-xs text-[var(--color-muted-foreground)]">
              <span>Avg: {formatMetricValue(avg, key)}</span>
              <span>Max: {formatMetricValue(max === -Infinity ? 0 : max, key)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Table View Component
// =============================================================================

interface TableViewProps {
  results: AggregationResult[];
  config: AggregationConfig;
}

function TableView({ results, config }: TableViewProps) {
  const metricKeys = config.metrics?.map((m) => m.alias) ?? ['count'];
  const dimensionKeys = config.group_by ?? [];

  return (
    <div className="max-h-[500px] overflow-auto rounded-lg border border-[var(--color-border)]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-[var(--color-secondary)]">
          <tr>
            <th className="whitespace-nowrap px-4 py-3 text-left font-medium text-[var(--color-foreground)]">
              Time
            </th>
            {dimensionKeys.map((key) => (
              <th
                key={key}
                className="whitespace-nowrap px-4 py-3 text-left font-medium text-[var(--color-foreground)]"
              >
                {key}
              </th>
            ))}
            {metricKeys.map((key) => (
              <th
                key={key}
                className="whitespace-nowrap px-4 py-3 text-right font-medium text-[var(--color-foreground)]"
              >
                {key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {results.map((result, index) => (
            <tr key={index} className="transition-colors hover:bg-[var(--color-muted)]/20">
              <td className="whitespace-nowrap px-4 py-3 text-[var(--color-muted-foreground)]">
                {formatTimestamp(result.bucket_start, config.time_bucket)}
              </td>
              {dimensionKeys.map((key) => (
                <td key={key} className="whitespace-nowrap px-4 py-3 text-[var(--color-foreground)]">
                  {String(result.dimensions[key] ?? '-')}
                </td>
              ))}
              {metricKeys.map((key) => (
                <td
                  key={key}
                  className="whitespace-nowrap px-4 py-3 text-right font-mono text-[var(--color-foreground)]"
                >
                  {formatMetricValue(result.metrics[key], key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Timeline Chart Component
// =============================================================================

interface TimelineChartProps {
  results: AggregationResult[];
  config: AggregationConfig;
}

function TimelineChart({ results, config }: TimelineChartProps) {
  const metricKeys = config.metrics?.map((m) => m.alias) ?? ['count'];
  const dimensionKeys = config.group_by ?? [];

  // Transform data for recharts
  const chartData = React.useMemo(() => {
    // If no dimensions, simple time series
    if (dimensionKeys.length === 0) {
      return results.map((r) => ({
        time: formatTimestamp(r.bucket_start, config.time_bucket),
        timestamp: new Date(r.bucket_start).getTime(),
        ...r.metrics,
      }));
    }

    // Group by time bucket and pivot dimensions
    const byTime = new Map<string, Record<string, number>>();
    for (const result of results) {
      const time = result.bucket_start;
      if (!byTime.has(time)) {
        byTime.set(time, {});
      }
      const entry = byTime.get(time)!;

      // Create dimension key for series name
      const dimKey = dimensionKeys.map((k) => result.dimensions[k] ?? 'null').join('-');

      // Add each metric with dimension prefix
      for (const metricKey of metricKeys) {
        const seriesKey = dimensionKeys.length > 0 ? `${dimKey}_${metricKey}` : metricKey;
        entry[seriesKey] = result.metrics[metricKey] ?? 0;
      }
    }

    return Array.from(byTime.entries())
      .map(([time, metrics]) => ({
        time: formatTimestamp(time, config.time_bucket),
        timestamp: new Date(time).getTime(),
        ...metrics,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [results, config, dimensionKeys, metricKeys]);

  // Get all series keys
  const seriesKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const data of chartData) {
      for (const key of Object.keys(data)) {
        if (key !== 'time' && key !== 'timestamp') {
          keys.add(key);
        }
      }
    }
    return Array.from(keys);
  }, [chartData]);

  if (chartData.length === 0) {
    return (
      <div className="flex h-[400px] items-center justify-center text-[var(--color-muted-foreground)]">
        No data to display
      </div>
    );
  }

  return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.3} />
          <XAxis
            dataKey="time"
            tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
            tickLine={{ stroke: 'var(--color-border)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
          />
          <YAxis
            tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
            tickLine={{ stroke: 'var(--color-border)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickFormatter={(value) => {
              if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
              if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
              return value;
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--color-popover)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              color: 'var(--color-foreground)',
            }}
            labelStyle={{ color: 'var(--color-foreground)', fontWeight: 'bold' }}
          />
          <Legend
            wrapperStyle={{ color: 'var(--color-foreground)' }}
            formatter={(value) => <span style={{ color: 'var(--color-foreground)' }}>{value}</span>}
          />
          {seriesKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={key}
              stroke={CHART_COLORS[index % CHART_COLORS.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: CHART_COLORS[index % CHART_COLORS.length] }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Bar Chart Component
// =============================================================================

interface BarChartViewProps {
  results: AggregationResult[];
  config: AggregationConfig;
}

function BarChartView({ results, config }: BarChartViewProps) {
  const metricKeys = config.metrics?.map((m) => m.alias) ?? ['count'];
  const dimensionKeys = config.group_by ?? [];

  // For bar chart, aggregate across time buckets by dimension
  const chartData = React.useMemo(() => {
    if (dimensionKeys.length === 0) {
      // No dimensions - show metrics per time bucket (limited to last N)
      return results.slice(-20).map((r) => ({
        name: formatTimestamp(r.bucket_start, config.time_bucket),
        ...r.metrics,
      }));
    }

    // Aggregate by first dimension
    const firstDimKey = dimensionKeys[0];
    const firstMetricKey = metricKeys[0] ?? 'count';

    const byDimension = new Map<string, Record<string, number>>();
    for (const result of results) {
      const dimKey = firstDimKey ? String(result.dimensions[firstDimKey] ?? 'null') : 'all';
      if (!byDimension.has(dimKey)) {
        byDimension.set(dimKey, {});
      }
      const entry = byDimension.get(dimKey)!;
      for (const metricKey of metricKeys) {
        entry[metricKey] = (entry[metricKey] ?? 0) + (result.metrics[metricKey] ?? 0);
      }
    }

    return Array.from(byDimension.entries())
      .map(([name, metrics]) => ({ name, ...metrics }) as { name: string } & Record<string, number>)
      .sort((a, b) => ((b[firstMetricKey] as number) ?? 0) - ((a[firstMetricKey] as number) ?? 0))
      .slice(0, 20);
  }, [results, config, dimensionKeys, metricKeys]);

  if (chartData.length === 0) {
    return (
      <div className="flex h-[400px] items-center justify-center text-[var(--color-muted-foreground)]">
        No data to display
      </div>
    );
  }

  return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.3} />
          <XAxis
            dataKey="name"
            tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
            tickLine={{ stroke: 'var(--color-border)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            interval={0}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
            tickLine={{ stroke: 'var(--color-border)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickFormatter={(value) => {
              if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
              if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
              return value;
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--color-popover)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              color: 'var(--color-foreground)',
            }}
            labelStyle={{ color: 'var(--color-foreground)', fontWeight: 'bold' }}
          />
          <Legend
            wrapperStyle={{ color: 'var(--color-foreground)' }}
            formatter={(value) => <span style={{ color: 'var(--color-foreground)' }}>{value}</span>}
          />
          {metricKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              name={key}
              fill={CHART_COLORS[index % CHART_COLORS.length]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Export to CSV
// =============================================================================

function exportToCsv(results: AggregationResult[], config: AggregationConfig) {
  const metricKeys = config.metrics?.map((m) => m.alias) ?? ['count'];
  const dimensionKeys = config.group_by ?? [];

  // Build CSV header
  const headers = ['bucket_start', 'bucket_end', ...dimensionKeys, ...metricKeys];
  const rows: string[] = [headers.join(',')];

  // Build CSV rows
  for (const result of results) {
    const row = [
      result.bucket_start,
      result.bucket_end,
      ...dimensionKeys.map((k) => String(result.dimensions[k] ?? '')),
      ...metricKeys.map((k) => String(result.metrics[k] ?? '')),
    ];
    rows.push(row.map((v) => `"${v}"`).join(','));
  }

  // Download
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${config.name}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =============================================================================
// Filter Panel Component
// =============================================================================

const LOG_LEVELS = ['debug', 'info', 'warn', 'error', 'fatal'] as const;
const LOG_TYPES = ['log', 'event', 'timing', 'metric', 'http', 'job', 'bug_report'] as const;

interface ContextFilter {
  key: string;
  value: string;
}

interface FilterPanelProps {
  appliedFilters?: AggregationFilters | null;
  onApply: (filters: AggregationFilters | null) => void;
  isComputing?: boolean;
}

function FilterPanel({ appliedFilters, onApply, isComputing }: FilterPanelProps) {
  const [isExpanded, setIsExpanded] = React.useState(!!appliedFilters);

  // Local filter state
  const [levels, setLevels] = React.useState<string[]>(appliedFilters?.levels ?? []);
  const [types, setTypes] = React.useState<string[]>(appliedFilters?.types ?? []);
  const [messageMatch, setMessageMatch] = React.useState(appliedFilters?.message_match ?? '');
  const [contextFilters, setContextFilters] = React.useState<ContextFilter[]>(() => {
    if (!appliedFilters?.context) return [];
    return Object.entries(appliedFilters.context).map(([key, value]) => ({
      key,
      value: String(value),
    }));
  });

  const hasActiveFilters = levels.length > 0 || types.length > 0 || messageMatch || contextFilters.length > 0;
  const hasAppliedFilters = appliedFilters && (
    (appliedFilters.levels?.length ?? 0) > 0 ||
    (appliedFilters.types?.length ?? 0) > 0 ||
    appliedFilters.message_match ||
    Object.keys(appliedFilters.context ?? {}).length > 0
  );

  const handleApply = () => {
    if (!hasActiveFilters) {
      onApply(null);
      return;
    }

    const filters: AggregationFilters = {};
    if (levels.length > 0) {
      filters.levels = levels as AggregationFilters['levels'];
    }
    if (types.length > 0) {
      filters.types = types as AggregationFilters['types'];
    }
    if (messageMatch.trim()) {
      filters.message_match = messageMatch.trim();
    }
    if (contextFilters.length > 0) {
      filters.context = {};
      for (const cf of contextFilters) {
        if (cf.key.trim() && cf.value.trim()) {
          // Try to parse as number or boolean
          const val = cf.value.trim();
          if (val === 'true') {
            filters.context[cf.key.trim()] = true;
          } else if (val === 'false') {
            filters.context[cf.key.trim()] = false;
          } else if (!isNaN(Number(val)) && val !== '') {
            filters.context[cf.key.trim()] = Number(val);
          } else {
            filters.context[cf.key.trim()] = val;
          }
        }
      }
      if (Object.keys(filters.context).length === 0) {
        delete filters.context;
      }
    }
    onApply(Object.keys(filters).length > 0 ? filters : null);
  };

  const handleClear = () => {
    setLevels([]);
    setTypes([]);
    setMessageMatch('');
    setContextFilters([]);
    onApply(null);
  };

  const toggleLevel = (level: string) => {
    setLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const toggleType = (type: string) => {
    setTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const addContextFilter = () => {
    setContextFilters((prev) => [...prev, { key: '', value: '' }]);
  };

  const updateContextFilter = (index: number, field: 'key' | 'value', value: string) => {
    setContextFilters((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current) {
        updated[index] = { key: current.key, value: current.value, [field]: value };
      }
      return updated;
    });
  };

  const removeContextFilter = (index: number) => {
    setContextFilters((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
      {/* Toggle Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <span>Filters</span>
          {hasAppliedFilters && (
            <span className="rounded-full bg-[var(--color-primary)] px-2 py-0.5 text-xs text-white">
              Active
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {/* Filter Content */}
      {isExpanded && (
        <div className="border-t border-[var(--color-border)] p-4 space-y-4">
          {/* Log Levels */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wider">
              Log Levels
            </label>
            <div className="flex flex-wrap gap-2">
              {LOG_LEVELS.map((level) => (
                <button
                  key={level}
                  onClick={() => toggleLevel(level)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    levels.includes(level)
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'bg-[var(--color-secondary)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                  )}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Log Types */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wider">
              Log Types
            </label>
            <div className="flex flex-wrap gap-2">
              {LOG_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    types.includes(type)
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'bg-[var(--color-secondary)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                  )}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Message Match */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wider">
              Message Pattern (regex)
            </label>
            <Input
              value={messageMatch}
              onChange={(e) => setMessageMatch(e.target.value)}
              placeholder="e.g., error|failed|exception"
              className="w-full"
            />
          </div>

          {/* Context Filters */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wider">
                Context Filters
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addContextFilter}
                className="h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>
            {contextFilters.length === 0 ? (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Add context filters like env=production, userId=123
              </p>
            ) : (
              <div className="space-y-2">
                {contextFilters.map((cf, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={cf.key}
                      onChange={(e) => updateContextFilter(index, 'key', e.target.value)}
                      placeholder="key (e.g., env)"
                      className="flex-1"
                    />
                    <span className="text-[var(--color-muted-foreground)]">=</span>
                    <Input
                      value={cf.value}
                      onChange={(e) => updateContextFilter(index, 'value', e.target.value)}
                      placeholder="value (e.g., production)"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeContextFilter(index)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--color-border)]">
            {hasAppliedFilters && (
              <Button variant="outline" size="sm" onClick={handleClear} disabled={isComputing}>
                Clear Filters
              </Button>
            )}
            <Button size="sm" onClick={handleApply} disabled={isComputing}>
              {isComputing ? (
                <>
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                  Applying...
                </>
              ) : (
                'Apply Filters'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function AggregationResultsViewer({
  results,
  config,
  timeRange,
  appliedFilters,
  onClose,
  onApplyFilters,
  isComputing,
  className,
}: AggregationResultsViewerProps) {
  const [viewMode, setViewMode] = React.useState<ViewMode>('timeline');

  const metricKeys = config.metrics?.map((m) => m.alias) ?? ['count'];

  // Auto-select best view mode based on data
  React.useEffect(() => {
    if (results.length === 0) return;

    const hasDimensions = (config.group_by?.length ?? 0) > 0;
    const hasMultipleTimeBuckets = results.length > 1;

    if (hasMultipleTimeBuckets) {
      setViewMode('timeline');
    } else if (hasDimensions) {
      setViewMode('bar');
    } else {
      setViewMode('table');
    }
  }, [results, config]);

  if (results.length === 0) {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[var(--color-foreground)]">
            {config.name} Results
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] py-12">
          <p className="text-[var(--color-muted-foreground)]">
            No results found for the selected time range.
          </p>
          <Button variant="outline" className="mt-4" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[var(--color-foreground)]">
            {config.name}
          </h3>
          {timeRange && (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {new Date(timeRange.from).toLocaleString()} - {new Date(timeRange.to).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCsv(results, config)}
            title="Export to CSV"
          >
            <Download className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">Export</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      {onApplyFilters && (
        <FilterPanel
          appliedFilters={appliedFilters}
          onApply={onApplyFilters}
          isComputing={isComputing}
        />
      )}

      {/* Summary Cards */}
      <MetricSummaryCards results={results} metricKeys={metricKeys} />

      {/* Chart/Table View */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        {viewMode === 'table' && <TableView results={results} config={config} />}
        {viewMode === 'timeline' && <TimelineChart results={results} config={config} />}
        {viewMode === 'bar' && <BarChartView results={results} config={config} />}
      </div>

      {/* Footer info */}
      <div className="flex justify-between text-xs text-[var(--color-muted-foreground)]">
        <span>
          {results.length} data point{results.length !== 1 ? 's' : ''} | Bucket: {config.time_bucket}
        </span>
        <span>
          Metrics: {metricKeys.join(', ')}
        </span>
      </div>
    </div>
  );
}

export default AggregationResultsViewer;
